import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { fetchPostDetail } from '../lib/queries.ts';
import type { PostDetail } from '../lib/types.ts';

function fmt(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusColor(status: string): string {
  switch (status) {
    case 'published':
      return 'var(--money, #4ADE80)';
    case 'scheduled':
      return 'var(--cool, #38BDF8)';
    case 'publishing':
      return 'var(--amber, #F59E0B)';
    case 'failed':
      return 'var(--hot, #FF3D6E)';
    default:
      return 'var(--text-3, #6B7283)';
  }
}

function StableVideo({ url }: { url: string | null }): React.ReactElement | null {
  const ref = useRef<string | null>(null);
  if (url && !ref.current) ref.current = url;
  const stable = ref.current;
  if (!stable) return null;
  return (
    <video
      src={stable}
      controls
      preload="metadata"
      style={{
        width: '100%',
        maxHeight: 480,
        borderRadius: 6,
        background: '#000',
        display: 'block',
      }}
    />
  );
}

export function PostDetailPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const locale = i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US';

  const query = useQuery({
    queryKey: ['post', id],
    queryFn: () => fetchPostDetail(id!),
    enabled: Boolean(id),
  });

  if (query.isLoading) {
    return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  }
  if (query.isError || !query.data) {
    return (
      <div style={{ padding: 24, color: 'var(--hot)' }}>
        Post not found.{' '}
        <button onClick={() => navigate('/posts')}>{t('common.back')}</button>
      </div>
    );
  }

  const post: PostDetail = query.data.post;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('post_detail.title')}</h1>
          <div className="sub" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
            id: {post.id}
          </div>
        </div>
        <div className="filters">
          <button onClick={() => navigate('/posts')}>← {t('post_detail.backToList')}</button>
        </div>
      </div>

      {/* Header summary */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 10,
            background: `color-mix(in srgb, ${statusColor(post.status)} 18%, transparent)`,
            color: statusColor(post.status),
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 700,
          }}
        >
          {post.status}
        </span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          {post.accountPlatform ? t(`accounts.platformShort.${post.accountPlatform}`) : '?'}{' '}
          {post.accountHandle ?? ''}
        </span>
        {post.campaignName && (
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-3)' }}>
            · {post.campaignName}
          </span>
        )}
        {post.platformPostUrl && (
          <a
            href={post.platformPostUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              color: 'var(--cool)',
              padding: '4px 10px',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              borderRadius: 4,
              textDecoration: 'none',
              marginLeft: 'auto',
            }}
          >
            ↗ {t('post_detail.openInPlatform')}
          </a>
        )}
      </div>

      {/* Video pipeline — 3 steps side by side */}
      <div className="form-section">
        <h3>{t('post_detail.videoPipeline')}</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            marginTop: 12,
          }}
        >
          <VideoSlot
            label={t('post_detail.step_source')}
            sublabel={t('post_detail.step_source_hint')}
            url={post.sourceVideoUrl}
          />
          <VideoSlot
            label={t('post_detail.step_generated')}
            sublabel={t('post_detail.step_generated_hint')}
            url={post.generatedVideoUrl}
          />
          <VideoSlot
            label={t('post_detail.step_final')}
            sublabel={t('post_detail.step_final_hint')}
            url={post.finalVideoUrl}
            highlight
          />
        </div>
      </div>

      {/* Caption */}
      <div className="form-section">
        <h3>{t('post_detail.caption')}</h3>
        <div
          style={{
            background: 'var(--bg-input)',
            padding: 12,
            borderRadius: 6,
            border: '1px solid var(--line)',
            fontSize: 14,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }}
        >
          {post.caption}
        </div>
      </div>

      {/* Script */}
      {post.draft?.script && (
        <div className="form-section">
          <h3>{t('post_detail.script')}</h3>
          <div
            style={{
              background: 'rgba(255,185,56,0.08)',
              border: '1px solid rgba(255,185,56,0.25)',
              borderRadius: 6,
              padding: 14,
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <strong style={{ color: '#FFB938' }}>Hook:</strong> {post.draft.script.hook}
            </div>
            <div style={{ marginBottom: 6 }}>
              <strong style={{ color: '#FFB938' }}>Body:</strong> {post.draft.script.body}
            </div>
            <div style={{ marginBottom: 6 }}>
              <strong style={{ color: '#FFB938' }}>CTA:</strong> {post.draft.script.cta}
            </div>
            {post.draft.script.hashtags.length > 0 && (
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  color: 'var(--text-3)',
                  marginTop: 8,
                }}
              >
                {post.draft.script.hashtags.map((h) => `#${h}`).join(' ')}
              </div>
            )}
            <div
              style={{
                marginTop: 10,
                padding: 10,
                background: 'rgba(108,192,255,0.1)',
                border: '1px solid rgba(108,192,255,0.3)',
                borderRadius: 4,
                fontFamily: 'var(--mono)',
                fontSize: 11,
                color: '#cfe8ff',
                lineHeight: 1.55,
                wordBreak: 'break-word',
              }}
            >
              <strong style={{ color: '#6CC0FF' }}>visual:</strong> {post.draft.script.visualPrompt}
            </div>
          </div>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              marginTop: 6,
            }}
          >
            {t('post_detail.scriptModel')}: {post.draft.scriptModel ?? '—'} ·{' '}
            {fmt(post.draft.scriptGeneratedAt, locale)}
          </div>
        </div>
      )}

      {/* Moderator hint */}
      {post.draft?.moderatorScriptHint && (
        <div className="form-section">
          <h3>{t('post_detail.moderatorHint')}</h3>
          <div
            style={{
              background: 'rgba(56,189,248,0.06)',
              border: '1px solid rgba(56,189,248,0.2)',
              borderRadius: 6,
              padding: 12,
              fontSize: 13,
              lineHeight: 1.5,
              fontStyle: 'italic',
            }}
          >
            {post.draft.moderatorScriptHint}
          </div>
        </div>
      )}

      {/* Transcript */}
      {post.draft?.sourceTranscript && (
        <div className="form-section">
          <h3>{t('post_detail.transcript')}</h3>
          <div
            style={{
              background: 'rgba(108,192,255,0.06)',
              border: '1px solid rgba(108,192,255,0.2)',
              borderRadius: 6,
              padding: 12,
              fontSize: 13,
              lineHeight: 1.55,
              fontStyle: 'italic',
              color: '#cfe8ff',
            }}
          >
            {post.draft.sourceTranscript}
          </div>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              marginTop: 6,
            }}
          >
            {post.draft.sourceTranscriptModel ?? 'whisper'}
          </div>
        </div>
      )}

      {/* Source meta */}
      {post.draft && (
        <div className="form-section">
          <h3>{t('post_detail.source_meta')}</h3>
          <table className="form-row" style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              <tr>
                <td style={{ padding: '4px 8px', color: 'var(--text-3)' }}>SRC URL</td>
                <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)', fontSize: 11 }}>
                  <a
                    href={post.draft.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--cool)' }}
                  >
                    {post.draft.sourceUrl}
                  </a>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '4px 8px', color: 'var(--text-3)' }}>Author</td>
                <td style={{ padding: '4px 8px' }}>{post.draft.sourceAuthorHandle ?? '—'}</td>
              </tr>
              <tr>
                <td style={{ padding: '4px 8px', color: 'var(--text-3)' }}>Source caption</td>
                <td style={{ padding: '4px 8px' }}>{post.draft.caption}</td>
              </tr>
              <tr>
                <td style={{ padding: '4px 8px', color: 'var(--text-3)' }}>Source views</td>
                <td style={{ padding: '4px 8px' }}>
                  {post.draft.sourceViewsCount?.toLocaleString(locale) ?? '—'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Schedule + metrics */}
      <div className="form-section">
        <h3>{t('post_detail.timing')}</h3>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-2)' }}>
          <div>
            <span style={{ opacity: 0.6 }}>{t('post_detail.scheduledFor')}:</span>{' '}
            {fmt(post.scheduledFor, locale)}
          </div>
          <div>
            <span style={{ opacity: 0.6 }}>{t('post_detail.publishedAt')}:</span>{' '}
            <span style={{ color: post.publishedAt ? 'var(--money)' : 'var(--text-3)' }}>
              {fmt(post.publishedAt, locale)}
            </span>
          </div>
          {post.platformPostId && (
            <div>
              <span style={{ opacity: 0.6 }}>{t('post_detail.platformPostId')}:</span>{' '}
              {post.platformPostId}
            </div>
          )}
          {(post.viewsCount > 0 || post.likesCount > 0) && (
            <div style={{ marginTop: 6 }}>
              👁 {post.viewsCount.toLocaleString(locale)} ·{' '}
              ❤ {post.likesCount.toLocaleString(locale)} ·{' '}
              💬 {post.commentsCount.toLocaleString(locale)}
            </div>
          )}
        </div>
        {post.failureReason && (
          <div
            style={{
              padding: 10,
              background: 'rgba(255,61,110,0.1)',
              border: '1px solid rgba(255,61,110,0.3)',
              borderRadius: 4,
              fontSize: 12,
              color: 'var(--hot)',
              fontFamily: 'var(--mono)',
              marginTop: 10,
            }}
          >
            {post.failureReason}
          </div>
        )}
      </div>
    </>
  );
}

function VideoSlot({
  label,
  sublabel,
  url,
  highlight = false,
}: {
  label: string;
  sublabel: string;
  url: string | null;
  highlight?: boolean;
}): React.ReactElement {
  return (
    <div
      style={{
        border: `1px solid ${highlight ? 'rgba(74, 222, 128, 0.4)' : 'var(--line)'}`,
        borderRadius: 6,
        padding: 10,
        background: highlight ? 'rgba(74, 222, 128, 0.04)' : 'var(--bg-card)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: highlight ? 'var(--money)' : 'var(--text-3)',
          fontWeight: 700,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>{sublabel}</div>
      {url ? (
        <StableVideo url={url} />
      ) : (
        <div
          style={{
            aspectRatio: '9 / 16',
            background: 'var(--bg-input)',
            border: '1px dashed var(--line)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-3)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            textAlign: 'center',
            padding: 12,
          }}
        >
          —
        </div>
      )}
    </div>
  );
}
